import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../common';
import { useAuth } from '../../contexts/AuthContext';
import { routes } from '../../lib/routes';
import TickGif from '../../assets/images/tick.gif';
import ProcessGif from '../../assets/images/process.gif';
import { MdOutlineArrowForward, MdRefresh } from 'react-icons/md';
import { useQueryClient } from '@tanstack/react-query';
import { DASHBOARD_KEY, MATCH_KEY } from '../../utils/query-keys';
import { useSocketEvent } from '../../hooks/useSocketEvent';
import { getMyMatches } from '../../services/matchService';

// Timeout after 5 minutes — pipeline takes ~4.5 min (persona + embeddings + LLM scoring)
const MATCH_SEARCH_TIMEOUT_MS = 5 * 60 * 1000;
// Check for matches every 10 seconds (fallback if WebSocket is broken)
const MATCH_CHECK_INTERVAL_MS = 10 * 1000;
const STORAGE_KEY_MATCHES_READY = 'matches_ready_state';

const MatchAlert = () => {
  const { user, checkStatus } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Persist matchesReady in sessionStorage to survive navigation
  const [matchesReady, setMatchesReady] = useState(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY_MATCHES_READY);
      if (stored) {
        const data = JSON.parse(stored);
        // Only use if for same user and not expired (1 hour)
        if (data.userId === user?.id && Date.now() - data.timestamp < 3600000) {
          return data.ready;
        }
      }
    } catch { /* ignore */ }
    return false;
  });
  const [matchesCount, setMatchesCount] = useState<number>(0);
  const [timedOut, setTimedOut] = useState(false);

  // Check for existing matches on mount and periodically (fallback for broken WebSocket)
  // BUG-011 FIX: Properly detect matches by checking matches_found flag
  // The API returns { matches: [...pending only...], matches_found: bool (based on total) }
  const checkForExistingMatches = useCallback(async () => {
    if (matchesReady || user?.onboarding_matches) return;

    try {
      const response = await getMyMatches({ page: 1, page_size: 10 });

      // BUG-011 FIX: Check matches_found flag (indicates ANY matches exist for user)
      // This is more reliable than checking matches.length because:
      // - matches array only contains PENDING matches
      // - matches_found is true if user has ANY matches (pending, approved, or declined)
      const matchesFound = response?.result?.matches_found === true;
      const pendingCount = response?.result?.matches?.length || 0;

      // Also check legacy response structures for backwards compatibility
      const legacyTotal = response?.result?.total || response?.result?.total_count || 0;

      // Consider matches ready if:
      // 1. matches_found flag is explicitly true, OR
      // 2. There are pending matches in the array, OR
      // 3. Legacy total count is > 0
      const hasMatches = matchesFound || pendingCount > 0 || legacyTotal > 0;

      if (hasMatches) {
        setMatchesReady(true);
        setTimedOut(false);
        // Show pending count (what user can actually interact with)
        setMatchesCount(pendingCount || legacyTotal);
        // Persist state
        try {
          sessionStorage.setItem(STORAGE_KEY_MATCHES_READY, JSON.stringify({
            ready: true,
            userId: user?.id,
            timestamp: Date.now(),
          }));
        } catch { /* ignore */ }
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY });
        queryClient.invalidateQueries({ queryKey: [...MATCH_KEY, 'onboardingMatches'] });
        // Refresh user data so onboarding_matches flag updates and banner dismisses
        try {
          await checkStatus();
        } catch { /* ignore */ }
      }
    } catch (error) {
      // Silently ignore - will retry on next interval
      console.debug('MatchAlert: Error checking for matches', error);
    }
  }, [matchesReady, user?.onboarding_matches, user?.id, queryClient]);

  // Check immediately on mount
  useEffect(() => {
    checkForExistingMatches();
  }, [checkForExistingMatches]);

  // Periodic check if matches not ready yet (WebSocket fallback)
  // UX-001 FIX: Continue checking even after timeout until matches are found
  useEffect(() => {
    // Stop interval only when matches are confirmed ready or user has seen them
    if (matchesReady || user?.onboarding_matches) return;

    const interval = setInterval(() => {
      // Re-check matches - this will reset timedOut if matches are found
      checkForExistingMatches();
    }, MATCH_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [matchesReady, user?.onboarding_matches, checkForExistingMatches]);

  // Timeout fallback - prevent infinite "in progress" state
  useEffect(() => {
    if (matchesReady || user?.onboarding_matches) return;

    const timeout = setTimeout(() => {
      if (!matchesReady) {
        setTimedOut(true);
      }
    }, MATCH_SEARCH_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [matchesReady, user?.onboarding_matches]);

  useSocketEvent('user.matches.ready', async data => {
    window.scrollTo(0, 0);

    if (data?.type === 'USER_MATCHES_READY') {
      setMatchesReady(true);
      setTimedOut(false);
      setMatchesCount(Number(data?.count) || 0);
      // Persist state so it survives navigation
      try {
        sessionStorage.setItem(STORAGE_KEY_MATCHES_READY, JSON.stringify({
          ready: true,
          userId: user?.id,
          timestamp: Date.now(),
        }));
      } catch { /* ignore */ }
      queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY });
      queryClient.invalidateQueries({
        queryKey: [...MATCH_KEY, 'onboardingMatches'],
      });
      // Refresh user data to update onboarding_matches flag
      try {
        await checkStatus();
      } catch (e) {
        // Ignore errors - user data will refresh on next page load
      }
    }
  });

  const handleCheckMatches = () => {
    // BUG-011 FIX: Navigate to regular matches page (with sidebar) not onboarding matches
    // User is already on dashboard, so they should see the main matches page
    navigate(routes.matches);
  };

  // Clear stored state when onboarding_matches is set (user has viewed matches)
  useEffect(() => {
    if (user?.onboarding_matches) {
      try {
        sessionStorage.removeItem(STORAGE_KEY_MATCHES_READY);
      } catch { /* ignore */ }
    }
  }, [user?.onboarding_matches]);

  if (user?.onboarding_matches) {
    return <></>;
  }

  return (
    <div className='px-3 mt-3'>
      <div className='p-3 flex sm:flex-row flex-col items-center justify-center gap-3 border-2 border-primary-25 rounded-xl'>
        {matchesReady ? (
          <>
            <img
              src={TickGif}
              alt='Complete'
              className='w-12 h-12 object-cover'
            />

            {matchesCount > 0 ? (
              <>
                <p className='text-md sm:text-base sm:text-md font-heading font-medium mb-0 sm:text-left text-center'>
                  We've got some matches! Just click to check them out.
                </p>
                <Button
                  className='w-fit min-w-fit'
                  size='sm'
                  variant='outline'
                  onClick={() => navigate(routes.matches)}
                >
                  View Matches
                  <MdOutlineArrowForward size={20} />
                </Button>
              </>
            ) : (
              <p className='text-md sm:text-base sm:text-md font-heading font-medium mb-0 sm:text-left text-center'>
                We couldn't find any matches right now. AI is working on it.
              </p>
            )}
          </>
        ) : timedOut ? (
          <>
            <p className='text-md font-heading font-medium mb-0 text-center'>
              Still processing your matches: this usually takes a few minutes.
            </p>
            <Button
              className='w-fit min-w-fit'
              size='sm'
              variant='outline'
              onClick={handleCheckMatches}
            >
              Check Matches
              <MdRefresh size={20} />
            </Button>
          </>
        ) : (
          <>
            <img
              src={ProcessGif}
              alt='Process'
              className='w-[10rem] h-[3rem] object-cover'
            />
            <p className='text-md font-heading font-medium mb-0'>
              Match search is in progress......
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default MatchAlert;
